'use client';

/**
 * The events are only to initiate analytics events and are not interactive from the
 * user perspective. These should only be used to trigger an interaction element on the link.
 * For everything else the Button component should be used.
 */
/* eslint-disable jsx-a11y/no-static-element-interactions -- See above comment. */
/* eslint-disable jsx-a11y/anchor-is-valid -- See above comment. */

import NextLink from 'next/link';
import type { FC } from 'react';

import { classes } from '@/next-utils/css-utils/scss-utils';
import { isNullish } from '@/utils/null-utils';
import { useInteraction } from '../../../hooks/useInteraction';
import { Box } from '../Box';
import { VisualButton } from '../Button/VisualButton';
import type { LinkProps } from './ILinkProps';

import S from './styles.module.scss';

/**
 * Get the core styling from the variant string.
 * @param variant - String representing a link styling type.
 * @returns Returns a className from the styling import.
 */
const getStylingFromVariant = (variant: string): string => {
  switch (variant) {
    case 'link':
      return S.link;
    case 'text':
      return S.text;
    case 'underline':
      return S.underline;
    case 'text-underline':
      return S.textUnderline;
    default:
      return '';
  }
};

/**
 * Customized next/link with analytics and composbale styling.
 */
export const Link: FC<LinkProps> = (props) => {
  const {
    href,
    className = '',
    passHref = false,
    variant = 'base',
    as = 'span',
    children,
    title,
    ariaLabel,
    ariaLabelledBy,
    openInNewTab,
    onClick,
    onKeyPress,
    interactionDetails,
    datatestID,
    id,
    style,
    tabIndex,
    prefetch = true
  } = props;

  const { onClickEvent, onKeyPressEvent } = useInteraction(
    onClick,
    onKeyPress,
    interactionDetails
  );

  // If button styling is requested, use VisualButton with NextLink
  if (props.variant === 'button' && !isNullish(href)) {
    const { buttonVariant, size, fullWidth, disabled, hover, loading } = props;

    return (
      <VisualButton
        id={id}
        className={className}
        variant={buttonVariant}
        size={size}
        fullWidth={fullWidth}
        disabled={disabled}
        hover={hover}
        loading={loading}
        title={title}
        ariaLabel={ariaLabel}
        onClick={onClickEvent}
        onKeyPress={onKeyPressEvent}
        style={style}
        interactionDetails={interactionDetails}
        datatestID={datatestID}
        as={
          <NextLink
            href={href}
            passHref={passHref}
            target={openInNewTab ? '_blank' : undefined}
            rel={openInNewTab ? 'noopener' : undefined}
            prefetch={prefetch}
          />
        }
      >
        {children}
      </VisualButton>
    );
  }

  const classString = classes(className, getStylingFromVariant(variant));

  if (!isNullish(href)) {
    return (
      <NextLink
        href={href}
        passHref={passHref}
        id={id}
        style={style}
        className={classString}
        title={title}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
        target={openInNewTab ? '_blank' : ''}
        rel={openInNewTab ? 'noopener' : ''}
        onClick={onClickEvent}
        onKeyUp={onKeyPressEvent}
        data-testid={datatestID}
        tabIndex={tabIndex}
      >
        {children}
      </NextLink>
    );
  }

  if (Boolean(as) || Boolean(className)) {
    return (
      <Box
        className={classString}
        as={as}
        title={title}
        ariaLabel={ariaLabel}
        onClick={onClickEvent}
        onKeyPress={onKeyPressEvent}
        data-testid={datatestID}
        style={style}
        id={id}
      >
        {children}
      </Box>
    );
  }

  return <>{children}</>;
};

export type { LinkProps };
